import { useFormikContext } from 'formik';
import React, { useRef, useState } from 'react';
import {
  Keyboard,
  Platform,
  ReturnKeyTypeOptions,
  TextInput,
  TextInputProps
} from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { useFormContext } from './Form';

export type FormInputVariant = 'outlined' | 'survey' | 'inline' | 'chart';

const FormInputView = styled.View`
  width: 100%;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledTextInputView = styled.View<{
  hasHelperText: boolean;
  error?: boolean;
  hasEndElement?: boolean;
}>`
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 16)}px;
  flex-direction: row;
  align-items: center;
  background-color: #fff;
  padding-right: ${p => (p.hasEndElement ? 12 : 0)}px;
`;

const StyledTextInput = styled.TextInput<{
  minHeight?: number;
  variant?: FormInputVariant;
  multiLine?: boolean;
  empty?: boolean;
  editable?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  ${p => (p.variant === 'survey' ? `font-size: 15px;` : `font-size: 18px;`)}
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  border-radius: 8px;
  padding: ${p =>
    p.variant === 'survey' && (Platform.OS === 'ios' || Platform.OS === 'web')
      ? '20px 16px'
      : '20px 12px'};
  line-height: 23px;
  flex: 1;
  opacity: ${p => (p.editable ? 1 : 0.6)};
  color: ${Colors.chloeBlack};
` as React.FC<
  TextInputProps & {
    minHeight?: number;
    variant?: FormInputVariant;
    multiLine?: boolean;
    ref: any;
    empty?: boolean;
  }
>;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 14px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)<{ errorTextColor: string }>`
  color: ${p =>
    p.errorTextColor === 'dark' ? Colors.error : Colors.errorCaption};
`;

export type FormInputProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  multiline?: boolean;
  returnKeyType?: ReturnKeyTypeOptions;
  submitOnReturn?: boolean;
  variant?: FormInputVariant;
  dismissKeyboard?: boolean;
  errorTextColor?: 'dark' | 'light';
  endElement?: JSX.Element;
};

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  helperText,
  multiline,
  returnKeyType,
  submitOnReturn,
  variant = 'outlined',
  dismissKeyboard,
  errorTextColor = 'light',
  endElement,
  editable = true,
  ...props
}) => {
  const textInputRef = useRef<TextInput>(null!);
  const { handleChange, handleBlur, touched, errors, values, submitForm } =
    useFormikContext<any>();
  const { setFocusedInput, addInput, focusNext } = useFormContext();
  const [height, setHeight] = useState<number>();
  const error = touched[name] && errors[name];

  return (
    <FormInputView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}
      <StyledTextInputView
        hasHelperText={Boolean(helperText)}
        error={Boolean(error)}
        hasEndElement={Boolean(endElement)}
      >
        <StyledTextInput
          multiline={multiline}
          blurOnSubmit={false}
          placeholderTextColor={error ? Colors.error : Colors.placeholderColor}
          ref={(textInput: TextInput) => {
            textInputRef.current = textInput;
            addInput(textInput);
          }}
          editable={editable}
          {...props}
          minHeight={height}
          variant={variant}
          value={values[name]}
          empty={!values[name]}
          returnKeyType={returnKeyType}
          onChangeText={text => {
            handleChange(name)(text);
            // Workaround for Android autofill issue
            if (Platform.OS === 'android') {
              const previousLength = String(values[name]).length;
              const currentLength = text.length;
              const isLikelyAutofill = currentLength > previousLength + 1;

              if (isLikelyAutofill) {
                // Force blur to trigger Formik validation
                setTimeout(() => {
                  textInputRef.current?.blur();
                }, 100);
              }
            }
          }}
          onFocus={() => {
            setFocusedInput(textInputRef.current);
          }}
          onBlur={event => {
            handleBlur(name)(event);
            setFocusedInput(undefined);
          }}
          onContentSizeChange={event => {
            setHeight(event.nativeEvent.contentSize.height);
          }}
          onSubmitEditing={() => {
            if (dismissKeyboard) {
              Keyboard.dismiss();
            } else if (submitOnReturn) {
              if (!multiline) {
                submitForm();
                Keyboard.dismiss();
              }
            } else {
              focusNext();
            }
          }}
        />
        {endElement}
      </StyledTextInputView>
      {error ? (
        <ErrorText errorTextColor={errorTextColor}>{error}</ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
    </FormInputView>
  );
};

export default FormInput;
